<template>
  <div>
    <template v-if="!readOnly">
      <AssigneesSearchPopover
        :disabled="loading"
        :maxSelectableAccounts="1"
        :multiple="false"
        :selectedAccountIds="normalizedResponseIds"
        @change="handleChange"
        @initialLoad="handleInitialLoad"
      >
        <ChipAvatar
          v-if="resolvedAccount"
          data-testid="accountForm-chipTrigger-selectedUser"
          disablePopover
          hideBorder
          :ripple="false"
          :account="resolvedAccount"
          :vuetifyProps="{ close: !loading }"
          @click:close="handleClear"
        />
        <div
          v-else-if="isHydratingEditableSelection"
          data-testid="accountForm-placeholderTrigger-loadingSelection"
          class="d-inline-flex align-center blue-grey--text text--darken-1 body-2"
        >
          <v-progress-circular indeterminate size="16" width="2" class="mr-2" />
          <span>{{ $t('common.loading') }}</span>
        </div>
        <v-btn
          v-else
          rounded
          small
          color="blue-grey lighten-3"
          elevation="0"
          :disabled="loading"
          data-testid="accountForm-buttonTrigger-selectUser"
        >
          <v-icon size="16" class="mr-1" color="blue-grey darken-1">
            mdi-plus
          </v-icon>
          <p class="ma-0 blue-grey--text text--darken-1 body-2">
            {{ $t('common.select_user') }}
          </p>
        </v-btn>
      </AssigneesSearchPopover>

      <v-text-field
        v-model="validationValue"
        class="field-to-validate-account-selection"
        :rules="validationRules"
        data-testid="accountForm-input-validationBridge"
      />
    </template>

    <div v-else-if="resolvedAccount">
      <ChipAvatar :account="resolvedAccount" />
    </div>

    <v-chip v-else small disabled class="label">
      {{ $t('checklist.no_response') }}
    </v-chip>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { utils } from 'frontend-js-shared'
import _ from 'lodash'

import api from '@/api'
import AssigneesSearchPopover from '@/components/AssigneesSearchPopover/AssigneesSearchPopover.vue'
import ChipAvatar from '@/components/ChipAvatar.vue'

const normalizeAccount = account => {
  const id = utils.getEntityId(account)

  return {
    id,
    _id: id,
    displayname: account?.displayname ?? '',
    familyname: account?.familyname ?? '',
    email: account?.email ?? '',
    photo: account?.photo ?? null,
    isDeleted: account?.isDeleted ?? false,
  }
}

export default {
  name: 'AccountForm',

  components: {
    AssigneesSearchPopover,
    ChipAvatar,
  },

  props: {
    response: {
      type: [Array, String],
      default: () => [],
    },
    loading: Boolean,
    rules: {
      type: Array,
      default: () => [],
    },
    readOnly: Boolean,
    accounts: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      resolvedAccounts: [],
      hasFetchedReadOnlyAccounts: false,
      hasFetchedEditableAccounts: false,
      isHydratingEditableSelection: false,
    }
  },

  computed: {
    ...mapGetters(['organizationId']),

    normalizedResponseIds() {
      if (Array.isArray(this.response)) {
        return this.response
          .map(value => utils.getEntityId(value))
          .filter(Boolean)
          .slice(0, 1)
      }

      if (typeof this.response === 'string' && this.response) {
        return [utils.getEntityId(this.response)].filter(Boolean)
      }

      return []
    },

    resolvedAccount() {
      return this.resolvedAccounts[0] ?? null
    },

    validationRules() {
      return this.rules.map(rule => value =>
        rule(value ? this.normalizedResponseIds : [])
      )
    },

    validationValue: {
      get() {
        return this.normalizedResponseIds.length ? 'selected' : ''
      },
      set() {},
    },
  },

  watch: {
    accounts: {
      immediate: true,
      async handler(value) {
        this.setResolvedAccounts(value, { preserveWhenEmpty: true })

        if (!this.readOnly) return
        if (this.resolvedAccounts.length > 0) return

        this.hasFetchedReadOnlyAccounts = false
        await this.fetchReadOnlyAccountsIfNeeded()
      },
    },

    response: {
      immediate: true,
      async handler() {
        this.syncResolvedAccountsWithResponse()
        await this.fetchEditableAccountsIfNeeded()
        await this.fetchReadOnlyAccountsIfNeeded()
      },
    },

    readOnly: {
      immediate: true,
      async handler() {
        await this.fetchReadOnlyAccountsIfNeeded()
      },
    },
  },

  methods: {
    accountMatchesSelectedId(account) {
      return utils.getEntityId(account) === this.normalizedResponseIds[0]
    },

    normalizeAccounts(accounts = [], selectedIds = this.normalizedResponseIds) {
      const selectedId = selectedIds[0]

      return accounts.map(normalizeAccount).filter(account => {
        return utils.getEntityId(account) === selectedId
      })
    },

    setResolvedAccounts(accounts = [], { preserveWhenEmpty = false } = {}) {
      const normalizedAccounts = this.normalizeAccounts(accounts)

      if (normalizedAccounts.length) {
        this.resolvedAccounts = normalizedAccounts
        return
      }

      if (!preserveWhenEmpty || !this.normalizedResponseIds.length) {
        this.resolvedAccounts = []
      }
    },

    handleInitialLoad(accounts = []) {
      this.setResolvedAccounts(accounts, { preserveWhenEmpty: true })
    },

    handleChange(payload = {}) {
      const ids = payload?.ids ?? []
      const items = payload?.items ?? []

      if (!Array.isArray(ids) || _.isEmpty(ids)) {
        this.$emit('change', [])
        this.resolvedAccounts = []
        return
      }

      const normalizedIds = ids
        .map(id => utils.getEntityId(id))
        .filter(Boolean)
        .slice(0, 1)

      this.resolvedAccounts = this.normalizeAccounts(items, normalizedIds)
      this.$emit('change', normalizedIds)
    },

    handleClear() {
      if (this.loading) return

      this.resolvedAccounts = []
      this.$emit('change', [])
    },

    syncResolvedAccountsWithResponse() {
      if (!this.normalizedResponseIds.length) {
        this.resolvedAccounts = []
        return
      }

      if (
        this.resolvedAccount &&
        this.accountMatchesSelectedId(this.resolvedAccount)
      ) {
        return
      }

      this.resolvedAccounts = []
    },

    async fetchEditableAccountsIfNeeded() {
      if (this.readOnly) return
      if (!this.normalizedResponseIds.length) return
      if (this.resolvedAccounts.length > 0) return

      this.hasFetchedEditableAccounts = false
      await this.fetchAccountsByIds({ editable: true })
    },

    async fetchReadOnlyAccountsIfNeeded() {
      if (!this.readOnly) return
      if (!this.normalizedResponseIds.length) return
      if (this.resolvedAccounts.length > 0) return
      await this.fetchAccountsByIds({ editable: false })
    },

    async fetchAccountsByIds({ editable }) {
      if (editable) {
        if (this.hasFetchedEditableAccounts) return
        this.hasFetchedEditableAccounts = true
        this.isHydratingEditableSelection = true
      } else {
        if (this.hasFetchedReadOnlyAccounts) return
        this.hasFetchedReadOnlyAccounts = true
      }

      try {
        const response = await api.account.getAccountsByIds(
          this.organizationId,
          {
            filters: { id: this.normalizedResponseIds },
            limit: this.normalizedResponseIds.length,
          }
        )

        const results = response?.data?.results ?? []
        this.setResolvedAccounts(results)
      } catch (error) {
        if (!editable) this.resolvedAccounts = []
      } finally {
        if (editable) this.isHydratingEditableSelection = false
      }
    },
  },
}
</script>

<style scoped>
.field-to-validate-account-selection :deep(.v-input__slot) {
  display: none !important;
}
</style>
